import { useMemo } from "react";
import { useRouterState } from "@tanstack/react-router";

export interface PageContext {
	pageType: string;
	pageId?: string;
	projectId?: string;
	projectName?: string;
	documentTitle?: string;
	contentTitle?: string;
}

/** True when the user has navigated to a different page/project/document. */
export function hasContextDrifted(a: PageContext, b: PageContext): boolean {
	return a.pageType !== b.pageType || a.pageId !== b.pageId;
}

export function usePageContext(): PageContext {
	const routerState = useRouterState();
	const pathname = routerState.location.pathname;
	const lastMatch = routerState.matches[routerState.matches.length - 1];
	const params = (lastMatch?.params ?? {}) as Record<string, string>;

	// CONTEXT: Depend on scalar values only — routerState.matches is a new
	// array reference on every router update, which would break useMemo stability
	// and cascade into transport/thread recreation.
	return useMemo(() => {
		if (pathname.startsWith("/project/") && params.projectId) {
			return {
				pageType: "project",
				projectId: params.projectId,
				pageId: params.projectId,
			};
		}

		if (pathname.startsWith("/doc/") && params.docId) {
			return {
				pageType: "document",
				pageId: params.docId,
			};
		}

		if (pathname.startsWith("/content/") && params.contentId) {
			return {
				pageType: "content",
				pageId: params.contentId,
			};
		}

		return { pageType: "home" };
	}, [pathname, params.projectId, params.docId, params.contentId]);
}
